import { supabase } from "../supabaseClient";

export const normalizeJob = (j) => ({
  id: j.id,
  company: j.company,
  title: j.title,
  location: j.location,
  type: j.type,
  salary: j.salary || '',
  description: j.description || '',
  requiredCerts: j.required_certs || [],
  preferredCerts: j.preferred_certs || [],
  posted: new Date(j.created_at).toLocaleDateString(),
  status: j.status,
});

export const normalizeTech = (t) => ({
  id: t.id,
  name: t.name,
  email: t.email || '',
  location: t.location,
  uxoHours: t.uxo_hours || '0',
  travel: t.travel,
  dodCerts: t.dod_certs || [],
  hazwoper40: t.hazwoper_40,
  hazwoper8: t.hazwoper_8,
  hazwoper8Supervisor: t.hazwoper_8_supervisor,
  physicalCurrent: t.physical_current,
  militaryEod: t.military_eod,
  clearance: t.clearance,
  clearanceLevel: t.clearance_level || '',
  diveCert: t.dive_cert,
  driversLicense: t.drivers_license,
  cdl: t.cdl,
  firstAidCpr: t.first_aid_cpr,
  totalFieldHours: t.total_field_hours || 0,
  updatedAt: t.updated_at || t.created_at || '',
  available: t.open_to_work,
  visibleToTechs: t.visible_to_techs ?? true,
  phone: t.phone || '',
  summary: t.summary || '',
  jobRolePreference: t.job_role_preference || 'any',
  specificRoles: t.specific_roles || [],
  resumePath: t.resume_path || null,
  certPaths: t.cert_paths || [],
  hazwoper8CertPath: t.hazwoper8_cert_path || null,
  physicalCertPath: t.physical_cert_path || null,
  // Expiry dates for cert status
  hazwoper8Expiry: t.hazwoper_8_expiry || null,
  firstAidCprExpiry: t.first_aid_cpr_expiry || null,
  physicalDate: t.physical_date || null,
  stateLicenseExpiry: t.state_license_expiry || null,
  // Logbook verification
  logbookVerifiedHours: t.logbook_verified_hours ?? null,
  logbookVerifiedSupervisoryHours: t.logbook_verified_supervisory_hours ?? null,
  logbookVerificationStatus: t.logbook_verification_status || 'unverified',
  logbookVerifiedAt: t.logbook_verified_at || null,
  logbookVerificationNotes: t.logbook_verification_notes || null,
  hasLogbook: t.has_logbook || false,
  supervisoryHours: t.supervisory_hours || 0,
  osha10Construction: t.osha_10_construction || false,
  qualityCredential: t.quality_credential || null,
  // Quality credential verification
  qualityCredentialStatus: t.quality_credential_status || 'unverified',
  qualityCredentialVerifiedAt: t.quality_credential_verified_at || null,
  qualityCredentialVerifiedNotes: t.quality_credential_verified_notes || null,
});

export const isExpired = (dateStr) => {
  if (!dateStr) return false;
  const diffDays = (new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24);
  return diffDays > 365;
};

export const isExpiringSoon = (dateStr) => {
  if (!dateStr) return false;
  const diffDays = (new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24);
  return diffDays > 335 && diffDays <= 365;
};

export const isOlderThanOneYear = (dateStr) => {
  if (!dateStr) return false;
  const diffDays = (new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24);
  return diffDays > 365;
};

// Returns "expired", "expiring", "active", or "unknown" for a cert expiry date
export const certExpiryStatus = (expiryDateStr) => {
  if (!expiryDateStr) return "unknown";
  const now = new Date();
  const expiry = new Date(expiryDateStr);
  const diffMs = expiry - now;
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays < 0) return "expired";
  if (diffDays <= 60) return "expiring";
  return "active";
};

// Returns "expired", "expiring", "active", or "unknown" for physical (expires 1 year after date)
export const physicalExpiryStatus = (issueDateStr) => {
  if (!issueDateStr) return "unknown";
  const issued = new Date(issueDateStr);
  const expiry = new Date(issued);
  expiry.setFullYear(expiry.getFullYear() + 1);
  return certExpiryStatus(expiry.toISOString().split('T')[0]);
};

// Check if a tech has any expired certs
export const hasAnyCertExpired = (tech) => {
  if (tech.hazwoper8 && certExpiryStatus(tech.hazwoper8Expiry) === "expired") return true;
  if (tech.firstAidCpr && certExpiryStatus(tech.firstAidCprExpiry) === "expired") return true;
  if (tech.physicalCurrent && physicalExpiryStatus(tech.physicalDate) === "expired") return true;
  if (tech.stateLicenseExpiry && certExpiryStatus(tech.stateLicenseExpiry) === "expired") return true;
  return false;
};

// Check if a tech has a specific cert (matching ALL_CERT_OPTIONS strings to tech fields)
export const techHasCert = (tech, cert) => {
  switch (cert) {
    case "HAZWOPER 40-Hour": return !!tech.hazwoper40;
    case "HAZWOPER 8-Hour Refresher": return !!tech.hazwoper8;
    case "HAZWOPER 8-Hour Supervisor": return !!tech.hazwoper8Supervisor;
    case "Current Physical": return !!tech.physicalCurrent;
    case "Security Clearance": return !!tech.clearance;
    case "Dive Certified": return !!tech.diveCert;
    case "Driver's License": return !!tech.driversLicense;
    case "CDL": return !!tech.cdl;
    case "EOD Background": return !!tech.militaryEod;
    case "First Aid/CPR": return !!tech.firstAidCpr;
    case "10-Hour OSHA Construction Safety": return !!tech.osha10Construction;
    case "Quality Credential": return !!tech.qualityCredential && tech.qualityCredential !== 'None';
    case "UXO Safety Officer":
    case "UXO Safety Officer / UXOSO":
      return (tech.dodCerts || []).some(c => c === "UXO Safety Officer" || c === "UXO Safety Officer / UXOSO");
    default: return (tech.dodCerts || []).includes(cert);
  }
};

// Calculate how well a tech matches a single job post
export const calcJobMatch = (tech, job) => {
  const required = job.requiredCerts || [];
  const preferred = job.preferredCerts || [];
  const metRequired = required.filter(c => techHasCert(tech, c));
  const missingRequired = required.filter(c => !techHasCert(tech, c));
  const meetsAllRequired = required.length === 0 || missingRequired.length === 0;
  const metPreferred = preferred.filter(c => techHasCert(tech, c));
  const preferredPct = preferred.length > 0 ? Math.round((metPreferred.length / preferred.length) * 100) : null;
  const hasNoRequirements = required.length === 0 && preferred.length === 0;
  return {
    jobId: job.id, jobTitle: job.title, company: job.company,
    meetsAllRequired, metRequired, missingRequired,
    required, preferred, metPreferred, preferredPct, hasNoRequirements,
  };
};

// Calculate best match across all employer job posts
export const calcBestMatch = (tech, jobs) => {
  if (!jobs || jobs.length === 0) return null;
  const matches = jobs.map(job => calcJobMatch(tech, job));
  // Sort: meets all required first, then by preferred pct
  matches.sort((a, b) => {
    if (a.meetsAllRequired !== b.meetsAllRequired) return a.meetsAllRequired ? -1 : 1;
    return (b.preferredPct || 0) - (a.preferredPct || 0);
  });
  return { best: matches[0], all: matches };
};

export const uploadFile = async (file, folder, userId) => {
  const fileName = `${userId}/${folder}/${Date.now()}_${file.name}`;
  const { error } = await supabase.storage.from('uxo-uploads').upload(fileName, file, { upsert: true });
  return error ? { path: null, error } : { path: fileName, error: null };
};
